// Source recommendation banner analytics events. `matched_*` fields
// reflect the BE-selected recommendation, not the user's current
// profile. `SourceRecommendationApplied` is emitted on both direct
// Apply (`is_customised: false`, empty diff arrays) and Save & Apply
// from the customise wizard (`is_customised: true`, diff vs offered
// list — feeds `sources.json` curation).

import type { BannerLocation } from '@snowtype/snowplow';

// Re-export the snowtype-generated alias under the local name so
// existing imports (e.g. `useSourceRecommendationBanner`) keep
// working without churn. The snowplow 2-0-0 schemas back this with
// the `banner_location` enum (`chat | popover | session`).
export type SourceRecommendationBannerLocation = BannerLocation;

/**
 * Defensive clamp for `removed_domains` / `added_domains` before
 * Snowplow emission. Mirrors the `maxItems: 1000` ceiling on both
 * arrays in `data-structures/com.heidi/source_recommendation_applied.yaml`
 * (`oscerai/snowplow` repo, schema `2-0-0`). The wizard already caps
 * saved domains at `MAX_DOMAINS_PER_COLLECTION = 300`, so the diff
 * arrays will not exceed 300 in practice — this clamp exists so the
 * event never gets silently dropped by the Iglu validator if either
 * the BE cap or the schema ceiling drifts in the future.
 */
export const SOURCE_RECOMMENDATION_DIFF_MAX_ITEMS = 1000;

/** Personalisation context attached to every banner event. */
interface SourceRecommendationPersonalisationProps {
  /** Country whose tag drove regional ordering. Empty when unresolved. */
  matched_country: string;
  /** BE-matched specialty id (may fall back to GP). `null` when unresolved. */
  matched_specialty_id: string | null;
  /** Human-readable name of `matched_specialty_id`. Empty during hydration. */
  matched_specialty_name: string;
}

export interface SourceRecommendationShownEventProps extends SourceRecommendationPersonalisationProps {
  banner_location: SourceRecommendationBannerLocation;
  /** Number of recommendations *offered* in the banner. */
  sources_count: number;
}

export interface SourceRecommendationDismissedEventProps extends SourceRecommendationPersonalisationProps {
  banner_location: SourceRecommendationBannerLocation;
  sources_count: number;
}

export interface SourceRecommendationAppliedEventProps extends SourceRecommendationPersonalisationProps {
  banner_location: SourceRecommendationBannerLocation;
  /** Pre-cap recommendation size (BE `total_domains`). */
  sources_count: number;
  /** Size of the entity now in source control. Equals `sources_count`
   *  when nothing was capped or customised. */
  total_domains_in_collection: number;
  /** True iff the user went through the customise wizard. */
  is_customised: boolean;
  /** Recommendation domains absent from the persisted entity. `[]` on
   *  direct-Apply; on customise this feeds down-weight curation. */
  removed_domains: string[];
  removed_domain_count: number;
  /** User-added domains not in the original recommendation. `[]` on
   *  direct-Apply; on customise this feeds gap-fill curation. */
  added_domains: string[];
  added_domain_count: number;
}

export interface SourceRecommendationCustomisedEventProps extends SourceRecommendationPersonalisationProps {
  banner_location: SourceRecommendationBannerLocation;
  sources_count: number;
}
